import { z } from "zod";
import { IsoDateTimeSchema } from "../common/primitives.js";
import { CardTypeSchema, GRADING_COMPANIES, GradeSchema, GradingCompanySchema, RAW_CONDITIONS, RawConditionSchema, ResearchPersonSchema, SortDirectionSchema, } from "../research/research.js";
/**
 * Portfolio (M4): the cards the business owns, entered by hand for the records, with photos.
 * Purchase prices are shown only to people allowed to see costs, and to whoever entered the card.
 */
/** At most this many photos per card; each at most PHOTO_MAX_BYTES. */
export const MAX_PHOTOS_PER_CARD = 6;
export const PHOTO_MAX_BYTES = 5 * 1024 * 1024;
export const PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];
const text = (label, max) => z
    .string()
    .trim()
    .max(max, `${label} must be at most ${String(max)} characters`)
    .nullish()
    .transform((v) => (v === undefined || v === null || v === "" ? null : v));
/** What is typed for a card. Blank boxes are stored as nothing. */
export const PortfolioItemInputSchema = z
    .object({
    name: z
        .string("Enter the card name")
        .trim()
        .min(1, "Enter the card name")
        .max(200, "Name must be at most 200 characters"),
    setName: text("Set", 200),
    cardNumber: text("Card number", 50),
    language: text("Language", 50),
    cardType: CardTypeSchema.default("raw"),
    condition: z
        .enum(RAW_CONDITIONS, "Choose the condition")
        .nullish()
        .transform((v) => v ?? null),
    gradingCompany: z
        .enum(GRADING_COMPANIES, "Choose the grading company")
        .nullish()
        .transform((v) => v ?? null),
    grade: GradeSchema.nullish().transform((v) => v ?? null),
    certificateNumber: text("Certificate number", 30),
    quantity: z
        .number("Enter how many")
        .int("Enter a whole number")
        .min(1, "At least 1")
        .max(10_000, "At most 10,000")
        .default(1),
    purchasePrice: z
        .string()
        .trim()
        .regex(/^\d{1,9}(\.\d{1,2})?$/, "Enter the price as a number, e.g. 12.50")
        .nullish()
        .or(z.literal(""))
        .transform((v) => (v === undefined || v === null || v === "" ? null : v)),
    purchaseCurrency: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z]{3}$/, "Choose the currency")
        .nullish()
        .or(z.literal(""))
        .transform((v) => (v === undefined || v === null || v === "" ? null : v)),
    purchaseDate: z.iso
        .date("Enter the date as YYYY-MM-DD")
        .nullish()
        .or(z.literal(""))
        .transform((v) => (v === undefined || v === null || v === "" ? null : v)),
    boughtFrom: text("Bought from", 200),
    storageLocation: text("Storage place", 200),
    notes: text("Notes", 2000),
    /** The research record this purchase came from, if any. */
    researchId: z
        .uuid()
        .nullish()
        .transform((v) => v ?? null),
})
    .superRefine((v, ctx) => {
    if (v.cardType === "graded") {
        if (v.gradingCompany === null)
            ctx.addIssue({ code: "custom", path: ["gradingCompany"], message: "Choose the grading company" });
        if (v.grade === null)
            ctx.addIssue({ code: "custom", path: ["grade"], message: "Enter the grade" });
    }
    if ((v.purchasePrice === null) !== (v.purchaseCurrency === null)) {
        ctx.addIssue({
            code: "custom",
            path: [v.purchasePrice === null ? "purchasePrice" : "purchaseCurrency"],
            message: v.purchasePrice === null ? "Enter the price for this currency" : "Choose the currency",
        });
    }
})
    // A raw card has no grade; a graded card has no raw condition.
    .transform((v) => v.cardType === "raw"
    ? { ...v, gradingCompany: null, grade: null, certificateNumber: null }
    : { ...v, condition: null });
/** POST /api/v1/portfolio */
export const CreatePortfolioItemRequestSchema = PortfolioItemInputSchema;
/** PUT /api/v1/portfolio/:id — replaces the details; `version` stops two people overwriting each other. */
export const UpdatePortfolioItemRequestSchema = z.object({
    item: PortfolioItemInputSchema,
    version: z.number().int().min(1),
});
export const PortfolioPhotoSchema = z.object({
    id: z.uuid(),
    contentType: z.enum(PHOTO_TYPES),
    sizeBytes: z.number().int().min(1),
    addedAt: IsoDateTimeSchema,
});
export const PortfolioItemSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    setName: z.string().nullable(),
    cardNumber: z.string().nullable(),
    language: z.string().nullable(),
    cardType: CardTypeSchema,
    condition: RawConditionSchema.nullable(),
    gradingCompany: GradingCompanySchema.nullable(),
    grade: z.number().nullable(),
    certificateNumber: z.string().nullable(),
    quantity: z.number().int().min(1),
    /**
     * Shown only to people allowed to see costs and to whoever entered the card (null otherwise);
     * `costHidden` says which.
     */
    purchasePrice: z.string().nullable(),
    purchaseCurrency: z.string().nullable(),
    costHidden: z.boolean(),
    purchaseDate: z.iso.date().nullable(),
    boughtFrom: z.string().nullable(),
    storageLocation: z.string().nullable(),
    notes: z.string().nullable(),
    researchId: z.uuid().nullable(),
    photos: z.array(PortfolioPhotoSchema),
    addedBy: ResearchPersonSchema,
    addedAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    version: z.number().int().min(1),
});
export const PortfolioItemResponseSchema = z.object({ item: PortfolioItemSchema });
export const PORTFOLIO_PAGE_SIZE = 25;
/** GET /api/v1/portfolio?q=venusaur&page=2 — newest first. */
/**
 * What the Portfolio list can be sorted by. Never by price: some people may not see costs,
 * and the order would give them away.
 */
export const PORTFOLIO_SORTS = ["added", "name", "set"];
export const PortfolioSortSchema = z.enum(PORTFOLIO_SORTS);
/** GET /api/v1/portfolio?q=charizard&cardType=graded&sort=name&dir=asc&page=2 — newest first by default. */
export const PortfolioListQuerySchema = z.object({
    q: z.string().trim().max(100, "Search is too long").optional(),
    cardType: CardTypeSchema.optional(),
    sort: PortfolioSortSchema.default("added"),
    dir: SortDirectionSchema.default("desc"),
    page: z.coerce.number().int().min(1).max(10_000).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(PORTFOLIO_PAGE_SIZE),
});
export const PortfolioListResponseSchema = z.object({
    data: z.array(PortfolioItemSchema),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    total: z.number().int().min(0),
});
//# sourceMappingURL=portfolio.js.map